import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import InputErrorMessage from '@trustoo/components/input/InputErrorMessage'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import { uploadKeywordMappingCsv } from 'components/keywordMappings/keywordMappingImportUpload'
import { useToast } from 'components/misc/Toaster'
import { KeywordMappingImport } from 'interfaces/KeywordMappingImport'
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react'

interface KeywordMappingImportModalProps {
    maxFileBytes: number
}

const CHOOSE_CSV = 'Choose a .csv file.'
const EMPTY_FILE = 'The file is empty.'
const START_FAILED = 'The import could not be started.'
const BINARY_MEGABYTE = 1024 * 1024
const DECIMAL_MEGABYTE = 1000 * 1000

const FORMAT_HELP =
    'The first row is a header with the columns market, service, keyword, searchIntent, h1_text and active, in any order. Extra columns, such as last_edited_by and last_edited_at from an export, are ignored. Use commas or semicolons, and UTF-8 or Windows-1252 (Excel). service is the service URL slug in that market (for example schilder). active is 1 or 0. Rows match on market, service and keyword: existing rows are updated and new rows are added. When a keyword appears twice, the later row wins.'

function megabyteLabel(bytes: number): string {
    const binary = bytes / BINARY_MEGABYTE
    if (Number.isInteger(binary)) return String(binary)

    const decimal = bytes / DECIMAL_MEGABYTE
    if (Number.isInteger(decimal)) return String(decimal)

    return binary.toLocaleString('nl-NL', { maximumFractionDigits: 1 })
}

function fileTooLargeMessage(maxFileBytes: number): string {
    return `The file is larger than ${megabyteLabel(maxFileBytes)} MB.`
}

function validateCsv(chosen: File, maxFileBytes: number): string | null {
    if (chosen.name.toLowerCase().endsWith('.csv') === false) return CHOOSE_CSV
    if (chosen.size < 1) return EMPTY_FILE
    if (chosen.size > maxFileBytes) return fileTooLargeMessage(maxFileBytes)
    return null
}

function messageFrom(caught: unknown): string {
    if (caught instanceof Error && caught.message !== '') return caught.message
    return START_FAILED
}

function displayedUploadPart(uploadedParts: number, partCount: number): number {
    const nextPart = uploadedParts + 1
    if (nextPart > partCount) return partCount
    if (nextPart < 1) return 1
    return nextPart
}

function uploadStatusText(uploadedParts: number, partCount: number): string {
    if (partCount < 1) return 'Uploading…'
    return `Uploading part ${displayedUploadPart(uploadedParts, partCount)} of ${partCount}`
}

function percentComplete(completed: number, total: number): number {
    if (total < 1) return 0
    const ratio = completed / total
    if (ratio < 0) return 0
    if (ratio > 1) return 100
    return Math.round(ratio * 100)
}

function toastForStartedImport(item: KeywordMappingImport): { msg: string; red?: boolean } {
    if (item.status === 'done') return { msg: 'Import finished.' }
    if (item.status === 'failed') {
        if (item.error_message === null || item.error_message === '') {
            return { msg: 'Import failed.', red: true }
        }
        return { msg: item.error_message, red: true }
    }
    return { msg: 'Import started. The report appears on this page when it is done.' }
}

interface UploadProgressBarProps {
    uploadedParts: number
    partCount: number
}

const UploadProgressBar = ({ uploadedParts, partCount }: UploadProgressBarProps) => {
    const valueNow = uploadedParts > partCount ? partCount : uploadedParts
    const percent = percentComplete(valueNow, partCount)

    return (
        <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={partCount}
            aria-valuenow={valueNow}
            aria-label="Upload progress"
            className="h-2 w-full overflow-hidden rounded bg-[#eaeaea]">
            <div className="h-full bg-[#5a8bfc]" style={{ width: `${percent}%` }} />
        </div>
    )
}

const KeywordMappingImportModal = NiceModal.create(
    ({ maxFileBytes }: KeywordMappingImportModalProps) => {
        const modal = useModal()
        const { addToast } = useToast()
        const activeRef = useRef(true)
        const [file, setFile] = useState<File | null>(null)
        const [error, setError] = useState('')
        const [isUploading, setIsUploading] = useState(false)
        const [uploadedParts, setUploadedParts] = useState(0)
        const [partCount, setPartCount] = useState(0)

        useEffect(() => {
            activeRef.current = true
            return () => {
                activeRef.current = false
            }
        }, [])

        const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
            const list = event.target.files
            if (list === null || list.length === 0) {
                setFile(null)
            } else {
                setFile(list.item(0))
            }
            setError('')
        }

        const runUpload = async (chosen: File) => {
            setIsUploading(true)
            setError('')
            setUploadedParts(0)
            setPartCount(0)

            try {
                const imported = await uploadKeywordMappingCsv(chosen, (uploaded, total) => {
                    if (activeRef.current === false) return
                    setUploadedParts(uploaded)
                    setPartCount(total)
                })
                if (activeRef.current === false) return
                addToast(toastForStartedImport(imported))
                modal.resolve(imported)
                modal.hide()
            } catch (caught: unknown) {
                if (activeRef.current === false) return
                setError(messageFrom(caught))
            } finally {
                if (activeRef.current) setIsUploading(false)
            }
        }

        const onSubmit = (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            if (isUploading) return

            if (file === null) {
                setError(CHOOSE_CSV)
                return
            }

            const validationMessage = validateCsv(file, maxFileBytes)
            if (validationMessage !== null) {
                setError(validationMessage)
                return
            }

            void runUpload(file)
        }

        const onCancel = () => {
            if (isUploading) return
            modal.resolve(undefined)
            modal.hide()
        }

        return (
            <BaseModal preventClose={isUploading} closeButtonLabel="Close">
                <form className="flex min-h-0 w-full flex-1 flex-col" onSubmit={onSubmit}>
                    <BaseModalHeader>
                        <h3 className="m-0">Import keyword mappings</h3>
                    </BaseModalHeader>
                    <BaseModalMainContent className="flex flex-col gap-4">
                        <p id="keyword-mapping-import-help" className="m-0 text-sm text-[#666666]">
                            {FORMAT_HELP}
                        </p>
                        <div>
                            <label
                                htmlFor="keyword-mapping-import-file"
                                className="mb-1 block text-sm font-medium">
                                CSV file
                            </label>
                            <input
                                id="keyword-mapping-import-file"
                                name="file"
                                type="file"
                                accept=".csv,text/csv"
                                disabled={isUploading}
                                aria-describedby="keyword-mapping-import-help keyword-mapping-import-error"
                                aria-invalid={error !== ''}
                                className="block w-full text-sm disabled:cursor-not-allowed disabled:opacity-60"
                                onChange={onFileChange}
                            />
                        </div>
                        {isUploading && (
                            <div className="flex flex-col gap-2">
                                {partCount > 0 && (
                                    <UploadProgressBar
                                        uploadedParts={uploadedParts}
                                        partCount={partCount}
                                    />
                                )}
                                <p className="m-0 text-sm" aria-live="polite">
                                    {uploadStatusText(uploadedParts, partCount)}
                                </p>
                            </div>
                        )}
                        <div id="keyword-mapping-import-error" aria-live="polite">
                            {error !== '' && <InputErrorMessage error={error} />}
                        </div>
                    </BaseModalMainContent>
                    <BaseModalFooter className="flex items-center justify-end gap-2">
                        <Button type="button" name="text" onClick={onCancel} disabled={isUploading}>
                            Cancel
                        </Button>
                        <Button type="submit" appearance="primary" disabled={isUploading}>
                            Start import
                        </Button>
                    </BaseModalFooter>
                </form>
            </BaseModal>
        )
    },
)

export default KeywordMappingImportModal
